import type { CmsBlock, CmsPage } from "@/types/cms";
import { FRAGMENT_PAGE_TYPE } from "./pageTypeRegistry";

export const HOMEPAGE_SLUG = "homepage";

export const TITLE_MAX_LENGTH = 60;

export const DESCRIPTION_MAX_LENGTH = 160;

const CARD_GRID_BLOCK_TYPE = "block_card_grid";

const LAUNCH_CARD_ID = "launch";

export type LaunchTileValues = {
  title: string;
  text: string;
  href: string;
};

export type LaunchTileErrors = Partial<Record<keyof LaunchTileValues, string>>;

export type HomepageContent = {
  block: CmsBlock;
  isPublished: boolean;
  launchTile: LaunchTileValues;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function isHomepageFragment(page: CmsPage): boolean {
  return page.slug === HOMEPAGE_SLUG && page.type === FRAGMENT_PAGE_TYPE;
}

function isLaunchCard(card: unknown): card is Record<string, unknown> {
  return isRecord(card) && card.id === LAUNCH_CARD_ID;
}

function readCards(block: CmsBlock): unknown[] | null {
  const cards = block.content?.cards;

  return Array.isArray(cards) ? cards : null;
}

function findCardGridBlock(blocks: CmsBlock[]): CmsBlock | null {
  const cardGridBlocks = blocks
    .filter(block => block.type === CARD_GRID_BLOCK_TYPE)
    .sort((first, second) => first.sortOrder - second.sortOrder);

  return cardGridBlocks[0] ?? null;
}

export function readHomepageContent(
  page: CmsPage | null,
  blocks: CmsBlock[],
): HomepageContent | null {
  if (!page || !isHomepageFragment(page)) {
    return null;
  }

  const block = findCardGridBlock(blocks);

  if (!block) {
    return null;
  }

  const launchCard = readCards(block)?.find(isLaunchCard);

  if (!launchCard) {
    return null;
  }

  return {
    block,
    isPublished: page.status === "published",
    launchTile: {
      title: asString(launchCard.title),
      text: asString(launchCard.text),
      href: asString(launchCard.href),
    },
  };
}

export function withLaunchTile(
  block: CmsBlock,
  values: LaunchTileValues,
): Record<string, unknown> | null {
  const cards = readCards(block);

  if (!cards?.some(isLaunchCard)) {
    return null;
  }

  const updatedCards = cards.map(card =>
    isLaunchCard(card)
      ? { ...card, title: values.title.trim(), text: values.text.trim(), href: values.href.trim() }
      : card,
  );

  return { ...block.content, cards: updatedCards };
}

const WHITESPACE_OR_CONTROL_CHARACTER = /[\s\p{Cc}]/u;

const ALLOWED_HOSTNAMES = new Set(["www.legal500.com", "legal500.com"]);

function isSitePath(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/\\");
}

function isAllowedFullUrl(href: string): boolean {
  let url: URL;

  try {
    url = new URL(href);
  } catch {
    return false;
  }

  return url.protocol === "https:" && ALLOWED_HOSTNAMES.has(url.hostname);
}

function isAllowedHref(href: string): boolean {
  if (WHITESPACE_OR_CONTROL_CHARACTER.test(href)) {
    return false;
  }

  if (href.startsWith("/")) {
    return isSitePath(href);
  }

  return isAllowedFullUrl(href);
}

export function validateLaunchTile(values: LaunchTileValues): LaunchTileErrors {
  const errors: LaunchTileErrors = {};

  const title = values.title.trim();
  const text = values.text.trim();

  if (title === "") {
    errors.title = "Title is required";
  } else if (title.length > TITLE_MAX_LENGTH) {
    errors.title = `Title must be ${TITLE_MAX_LENGTH} characters or fewer`;
  }

  if (text === "") {
    errors.text = "Description is required";
  } else if (text.length > DESCRIPTION_MAX_LENGTH) {
    errors.text = `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`;
  }

  const href = values.href.trim();

  if (href === "") {
    errors.href = "Link is required";
  } else if (!isAllowedHref(href)) {
    errors.href = "Use a legal500.com link or a path starting with /";
  }

  return errors;
}
